import { call, put, select, takeLatest } from 'redux-saga/effects'
import i18n from '../../../i18n'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { NotificationCenterService } from '../services/notificationCenter.service'
import { NotificationCenterConstants } from '../store/notificationCenter.constants'
import { setNotifications, setNotificationsLoading, setReadFlagIds } from '../store/notificationCenter.actions'
import { INotificationCenterResponse, INotificationListItem } from '../store/notificationCenter.types'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { setErrorMessage, showToast } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import Logger from '../../../services/logger.service'
import { isOffline } from '../../../utilities'
import LocalStorageService from '../../../services/localStorage.service'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { getUserSelector } from '../../user/selectors'

export function* getNotifications() {
  if (isOffline()) {
    yield put(setErrorMessage(i18n.t('notifications.errors.offline')))
    yield put(showToast(true))
    return
  }

  yield put(setNotificationsLoading(true))

  try {
    const authenticationJwt: string = yield select(getAuthenticationJwtSelector)

    const notificationCenterService = new NotificationCenterService(authenticationJwt)

    const response: INotificationCenterResponse = yield call([
      notificationCenterService,
      notificationCenterService.fetchNotifications,
    ])
    if (response.status === 200) {
      const notificationsData: INotificationListItem[] = (response.data?.qaFlagRecords || []).map((record) => ({
        id: record.id,
        prid: record.prid,
        createdAt: record.createdAt,
        isRead: false,
        flagType: record.flagType,
        lockType: record.lockType,
      }))

      const { userId, databaseUserName } = yield select(getUserSelector)

      const stored = LocalStorageService.getItemForUserIdElseDatabaseUserName(userId, databaseUserName, 'readFlagIds')
      const readFlagIds: number[] = stored ? JSON.parse(stored) : []

      yield put(setReadFlagIds(readFlagIds))
      yield put(
        setNotifications(
          notificationsData.map((item) => ({
            ...item,
            isRead: readFlagIds.includes(item.id),
          }))
        )
      )
      Logger.info(`Notification Center: fetchNotifications success, count: ${notificationsData.length}`)
    } else {
      Logger.error(`Notification Center: fetchNotifications failed with status ${response.status}`)

      response.failure = true
      response.responseMessage = i18n.t('notifications.errors.loadFlagsError')

      yield put(handleApiError(response))
    }
  } catch (e: any) {
    const cause = e.message ? e.message : 'unknown'

    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: i18n.t('notifications.errors.loadFlagsFailed', { cause }),
        },
      ],
    }

    yield put(handleApiError(errorResponse))
  } finally {
    yield put(setNotificationsLoading(false))
  }
}

export function* getNotificationsSaga() {
  yield takeLatest([NotificationCenterConstants.FETCH_NOTIFICATIONS], performWithMutexHOG, getNotifications)
}
